import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useState } from 'react'
import { useDropzone, FileRejection, DropzoneRootProps, DropzoneInputProps, Accept } from 'react-dropzone'
import './CatalogItemList.css'
import { catalogModTypeList } from './catalogTypes'
import { UploadFile, DescriptionOutlined, Close } from '@mui/icons-material'
import axios from 'axios'
import { DatePicker } from '@impres-development/assets/client'
import { Dayjs } from 'dayjs'

interface UploadProps {
  contractNumber: string
  onUploadComplete?: (
    uploads: { file: File; modOptions: { modType?: string; modDate?: Dayjs }; response: unknown }[],
  ) => void
  maxFiles?: number
  maxSize?: number
  uploadUrl?: string
  accept?: Accept
  open?: boolean
  onClose?: () => void
  failedMessage?: string | null
  title?: string
  description?: string
  allowText?: boolean
}

type modUpload = {
  file: File
  progress: number
  status: 'ready' | 'uploading' | 'done' | 'error'
  modOptions: { modType?: string; modDate?: Dayjs }
}

function DropzoneBox({
  onChooseFiles,
  isDragActive = false,
  rootProps,
  inputProps,
}: {
  onChooseFiles: () => void
  isDragActive?: boolean
  rootProps?: DropzoneRootProps
  inputProps?: DropzoneInputProps
}) {
  return (
    <div {...rootProps} className={`dropzone ${isDragActive ? 'active' : ''}`}>
      {inputProps && <input {...inputProps} />}
      <div className="dropzone-prompt">
        {isDragActive ? (
          <span>
            <UploadFile /> Drop File Here
          </span>
        ) : (
          <>
            <span>
              <UploadFile /> Drop File to Upload
            </span>
            <span>or</span>
          </>
        )}
        <Button variant="contained" onClick={onChooseFiles}>
          Choose File
        </Button>
      </div>
    </div>
  )
}

function MiddleTruncate({ text, tailChars = 14 }: { text: string; tailChars?: number }) {
  const splitAt = Math.max(text.length - tailChars, 0)

  return (
    <>
      <span className="middle-truncate-head">{text.slice(0, splitAt)}</span>
      <span className="middle-truncate-tail">{text.slice(splitAt)}</span>
    </>
  )
}

function FileRow({
  upload,
  contractNumber,
  modFailed,
  failedMessage,
  onModOptionsChange,
  onDelete,
}: {
  upload: modUpload
  contractNumber: string
  modFailed: boolean
  failedMessage: string | null
  onModOptionsChange: (options: { modType?: string; modDate?: Dayjs }) => void
  onDelete: () => void
}) {
  const { file, progress, status, modOptions } = upload

  return (
    <div className="file-row">
      <DescriptionOutlined />
      <div className="file-row-info">
        <Typography variant="body2" className="file-row-name">
          <MiddleTruncate text={file.name} />
        </Typography>
        {status === 'uploading' ? (
          <>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption">Uploading</Typography>
          </>
        ) : status === 'error' ? (
          <Typography variant="caption" color="error">
            Upload failed
          </Typography>
        ) : (
          <Typography variant="caption">{(file.size / 1024).toFixed(2)} KB</Typography>
        )}
      </div>
      {contractNumber === 'gs-35f-0002v' && (
        <div className="file-row-modtype">
          <DatePicker
            label="MOD Date"
            size="small"
            value={modOptions.modDate}
            onChange={date => onModOptionsChange({ ...modOptions, modDate: date ?? undefined })}
          />
          <TextField
            select
            fullWidth
            size="small"
            label="MOD Type"
            value={modOptions.modType}
            onChange={e => onModOptionsChange({ ...modOptions, modType: e.target.value })}
          >
            {catalogModTypeList.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </div>
      )}
      {contractNumber === 'ar3227' && (
        <div className="file-row-modtype">
          <DatePicker
            label="MOD Date"
            size="small"
            value={modOptions.modDate}
            onChange={date => onModOptionsChange({ ...modOptions, modDate: date ?? undefined })}
          />
        </div>
      )}
      {modFailed && failedMessage && (
        <Typography variant="caption" color="error">
          {failedMessage}
        </Typography>
      )}
      <IconButton onClick={onDelete}>
        <Close />
      </IconButton>
    </div>
  )
}

export default function DragAndDropUpload({
  onUploadComplete,
  contractNumber,
  maxFiles = 20,
  maxSize = 64000000, // 64 MB
  uploadUrl,
  accept = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    'text/csv': ['.csv'],
  },
  open: dialogOpen,
  onClose,
  failedMessage = 'Modification failed to apply',
  title = 'Upload File',
  description,
  allowText = false,
}: UploadProps) {
  const [uploads, setUploads] = useState<modUpload[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [chooseFilesOpen, setChooseFilesOpen] = useState(false)
  const [modApplying, setModApplying] = useState(false)
  const [modFailed, setModFailed] = useState(false)
  const [text, setText] = useState('')

  const handleDelete = useCallback((file: File) => {
    setUploads(prev => prev.filter(u => u.file !== file))
  }, [])

  const uploadFile = useCallback(
    async (upload: modUpload) => {
      const { file, modOptions } = upload
      const modType = modOptions.modType

      if (contractNumber === 'gs-35f-0002v' && !modType) {
        throw new Error('A modification type is required before uploading')
      }

      const formData = new FormData()
      formData.append('file', file)
      if (modType) formData.append('modType', modType)
      else if (contractNumber === 'ar3227') formData.append('modType', 'Cisco HW & SW Pricelist')
      if (modOptions.modDate) formData.append('modDate', modOptions.modDate.toISOString())

      try {
        const res = await axios.post(uploadUrl ?? `/catalog/${contractNumber}/modifications`, formData, {
          onUploadProgress: e => {
            const percent = e.total ? Math.round((e.loaded / e.total) * 100) : 0
            setUploads(prev => prev.map(u => (u.file === file ? { ...u, progress: percent } : u)))
          },
        })
        setUploads(prev => prev.map(u => (u.file === file ? { ...u, status: 'done' } : u)))
        return res.data as unknown
      } catch (err) {
        setUploads(prev => prev.map(u => (u.file === file ? { ...u, status: 'error' } : u)))
        throw err
      }
    },
    [contractNumber, uploadUrl],
  )

  const onDropAccepted = useCallback((acceptedFiles: File[]) => {
    setUploadError(null)
    setUploads(
      acceptedFiles.map(file => ({
        file,
        progress: 0,
        status: 'ready' as const,
        modOptions: { modType: '', modDate: undefined },
      })),
    )
  }, [])

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    const errors = fileRejections.map(rejection => `${rejection.file.name}: ${rejection.errors[0].message}`)
    setUploadError(errors.join('\n'))
  }, [])

  const handleCancel = () => {
    setUploads([])
    setUploadError(null)
    setChooseFilesOpen(false)
    onClose?.()
    setText('')
  }

  const handleSubmit = async () => {
    setModApplying(true)
    setModFailed(false)

    if (allowText && text.length > 0) {
      axios
        .post(uploadUrl ?? `/catalog/${contractNumber}/modifications`, { text })
        .then(res => {
          handleCancel()
          onUploadComplete?.([
            { file: new File([text], 'text.txt'), modOptions: { modType: '', modDate: undefined }, response: res.data },
          ])
        })
        .catch(err => {
          console.error('Failed to upload mod', axios.isAxiosError(err) ? err.response?.data ?? err : err)
          setModFailed(true)
          setUploadError(axios.isAxiosError(err) ? err.response?.data?.message ?? null : null)
        })
        .finally(() => setModApplying(false))
    } else {
      setUploads(prev => prev.map(upload => ({ ...upload, progress: 0, status: 'uploading' })))

      try {
        const responses = await Promise.all(uploads.map(uploadFile))
        handleCancel()
        onUploadComplete?.(uploads.map((u, i) => ({ file: u.file, modOptions: u.modOptions, response: responses[i] })))
      } catch (err) {
        console.error('Failed to upload mod', axios.isAxiosError(err) ? err.response?.data ?? err : err)
        setModFailed(true)
        setUploadError(axios.isAxiosError(err) ? err.response?.data?.message ?? null : null)
      } finally {
        setModApplying(false)
      }
    }
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxFiles,
    maxSize,
    accept,
    noClick: true,
  })

  const canSubmit =
    (uploads.length > 0 &&
      uploads.every(
        u =>
          (u.status === 'ready' || u.status === 'error') &&
          (contractNumber === 'gs-35f-0002v' ? u.modOptions.modDate && u.modOptions.modType : true),
      ) &&
      !modApplying) ||
    (allowText && text.length > 0)

  return (
    <>
      {dialogOpen === undefined && (
        <div className="upload-container">
          <DropzoneBox onChooseFiles={() => setChooseFilesOpen(true)} />
        </div>
      )}
      <Dialog
        className="upload-dialog"
        fullWidth
        open={dialogOpen ?? chooseFilesOpen}
        onClose={modApplying ? undefined : handleCancel}
        maxWidth="sm"
      >
        <DialogTitle>{title}</DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap="16px">
            {description && <DialogContentText>{description}</DialogContentText>}
            <DropzoneBox
              onChooseFiles={open}
              isDragActive={isDragActive}
              rootProps={getRootProps()}
              inputProps={getInputProps()}
            />
            {uploads.map(upload => (
              <FileRow
                key={upload.file.name}
                contractNumber={contractNumber}
                upload={upload}
                onModOptionsChange={value =>
                  setUploads(prev => prev.map(u => (u.file === upload.file ? { ...u, modOptions: value } : u)))
                }
                onDelete={() => handleDelete(upload.file)}
                modFailed={modFailed}
                failedMessage={failedMessage}
              />
            ))}

            {uploadError && (
              <Typography variant="caption" color="error">
                {uploadError}
              </Typography>
            )}

            {allowText && (
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Alternatively, paste content here"
                value={text}
                onChange={e => setText(e.target.value)}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleCancel} disabled={modApplying}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit} loading={modApplying}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
